<div class="fcomp-shell fcomp-ap-page" (click)="closeMenu()">

  <!-- Page header -->
  <header class="fcomp-header">
    <div>
      <h2 class="fcomp-header__title">{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.TITLE' | translate }}</h2>
      <p class="fcomp-header__meta">{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.SUBTITLE' | translate }}</p>
    </div>
    <div class="fcomp-ap-header-actions" *ngIf="!loading && totalCount > 0">
      <button type="button" class="fcomp-ap-summary__export"
              (click)="exportCsv($event)"
              [disabled]="totalCount === 0 || exporting"
              [attr.title]="'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.EXPORT_CSV' | translate">
        <i class="pi pi-download"></i>
        {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.EXPORT_CSV' | translate }}
      </button>
      <!-- XLSX sits beside the CSV rather than replacing it: the CSV is a plain
           data dump anything can parse, this one is a presentation document
           carrying the business's logo. Generated server-side, so it shows a
           spinner while the request is in flight. -->
      <button type="button" class="fcomp-ap-summary__export"
              (click)="exportXlsx($event)"
              [disabled]="totalCount === 0 || exportingXlsx"
              [attr.title]="'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.EXPORT_XLSX' | translate">
        <i class="pi" [ngClass]="exportingXlsx ? 'pi-spin pi-spinner' : 'pi-file-excel'"></i>
        {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.EXPORT_XLSX' | translate }}
      </button>
    </div>
  </header>

  <!-- ===================================================================
       Filter bar. One view over one dataset: this replaced All/Recurring
       sub-tabs, which were a filter dressed as navigation and hid the
       interval filter inside one of them.

       Filter states are deliberately purple-only. The ROWS use colour as
       data (blue open, amber in-progress, green done, amber skipped-cycle,
       red overdue); if the controls reused those hues they would read as
       data, and an active red "overdue" filter would vanish into the red
       rows it produces. Active state is carried by fill + weight + the
       summary line below, never by hue alone.
       =================================================================== -->
  <!-- Gated on allCount (does this business have ANY action points), never on
       `rows` — `rows` is the filtered page, so keying the bar on it made a
       filter that matched nothing unmount the control that applied it. Picking
       an interval is the narrowest filter here, so that is where it showed:
       the bar disappeared and the filter could no longer be undone. -->
  <div class="fcomp-ap-filters" *ngIf="!loading && counts.allCount > 0">

    <!-- Type: mutually exclusive, so a segmented control rather than chips. -->
    <div class="fcomp-ap-filter-group">
      <span class="fcomp-ap-filter-group__label">
        {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.FILTER_TYPE' | translate }}
      </span>
      <div class="fcomp-ap-segmented" role="group">
        <button type="button"
                class="fcomp-ap-segmented__option"
                [class.is-active]="typeFilter === 'all'"
                [attr.aria-pressed]="typeFilter === 'all'"
                (click)="setTypeFilter('all')">
          {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.TYPE_ALL' | translate }}
          <!-- allCount, NOT rows.length: rows is one page, so this chip read
               "All 10" next to "One-off 15" and "Showing 1-10 of 16". -->
          <span class="fcomp-ap-segmented__count">{{ allCount }}</span>
        </button>
        <button type="button"
                class="fcomp-ap-segmented__option"
                [class.is-active]="typeFilter === 'oneOff'"
                [attr.aria-pressed]="typeFilter === 'oneOff'"
                (click)="setTypeFilter('oneOff')">
          {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.TYPE_ONE_OFF' | translate }}
          <span class="fcomp-ap-segmented__count">{{ oneOffCount }}</span>
        </button>
        <button type="button"
                class="fcomp-ap-segmented__option"
                [class.is-active]="typeFilter === 'recurring'"
                [attr.aria-pressed]="typeFilter === 'recurring'"
                (click)="setTypeFilter('recurring')">
          <i class="pi pi-refresh"></i>
          {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.TYPE_RECURRING' | translate }}
          <span class="fcomp-ap-segmented__count">{{ recurringCount }}</span>
        </button>
      </div>
    </div>

    <!-- Status: independently toggleable, because "open AND in progress"
         (everything outstanding) is the view people actually want. -->
    <div class="fcomp-ap-filter-group">
      <span class="fcomp-ap-filter-group__label">
        {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_STATUS' | translate }}
      </span>
      <div class="fcomp-ap-chips">
        <button type="button" class="fcomp-ap-filter-chip"
                [class.is-active]="isStatusActive(ApStatus.Open)"
                [attr.aria-pressed]="isStatusActive(ApStatus.Open)"
                (click)="toggleStatusFilter(ApStatus.Open)">
          {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.STATUS_OPEN' | translate }}
          <span class="fcomp-ap-filter-chip__count">{{ openCount }}</span>
        </button>
        <button type="button" class="fcomp-ap-filter-chip"
                [class.is-active]="isStatusActive(ApStatus.InProgress)"
                [attr.aria-pressed]="isStatusActive(ApStatus.InProgress)"
                (click)="toggleStatusFilter(ApStatus.InProgress)">
          {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.STATUS_IN_PROGRESS' | translate }}
          <span class="fcomp-ap-filter-chip__count">{{ inProgressCount }}</span>
        </button>
        <button type="button" class="fcomp-ap-filter-chip"
                [class.is-active]="isStatusActive(ApStatus.Completed)"
                [attr.aria-pressed]="isStatusActive(ApStatus.Completed)"
                (click)="toggleStatusFilter(ApStatus.Completed)">
          {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.STATUS_COMPLETED' | translate }}
          <span class="fcomp-ap-filter-chip__count">{{ completedCount }}</span>
        </button>
        <!-- Only once something has actually been written off. A business on
             its first review has no rejected work, and a permanent chip
             reading zero would just invite a click that shows nothing. -->
        <button type="button" class="fcomp-ap-filter-chip" *ngIf="rejectedCount > 0"
                [class.is-active]="isStatusActive(ApStatus.Rejected)"
                [attr.aria-pressed]="isStatusActive(ApStatus.Rejected)"
                (click)="toggleStatusFilter(ApStatus.Rejected)">
          {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.STATUS_REJECTED' | translate }}
          <span class="fcomp-ap-filter-chip__count">{{ rejectedCount }}</span>
        </button>
      </div>
    </div>

    <!-- Findings: which checkpoint rating the commitment answers for. This is
         the axis that turns the action plan into a worklist — "show me what is
         still Mangler" — so it sits beside Status rather than inside a menu. -->
    <div class="fcomp-ap-filter-group" *ngIf="missingCount > 0 || needsImprovementCount > 0">
      <span class="fcomp-ap-filter-group__label">
        {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.FILTER_FINDING' | translate }}
      </span>
      <div class="fcomp-ap-chips">
        <button type="button" class="fcomp-ap-filter-chip"
                *ngFor="let f of allFindings"
                [ngClass]="findingToneClass(f)"
                [class.is-active]="isFindingActive(f)"
                [attr.aria-pressed]="isFindingActive(f)"
                (click)="toggleFindingFilter(f)">
          {{ findingKey(f) | translate }}
          <span class="fcomp-ap-filter-chip__count">{{ findingCount(f) }}</span>
        </button>
      </div>
    </div>

    <!-- Interval: a dropdown, not eight permanent chips. Always available —
         picking one sets the Recurring segment for you. -->
    <div class="fcomp-ap-filter-group" *ngIf="recurringCount > 0">
      <span class="fcomp-ap-filter-group__label">
        {{ 'FLEXI_COMPLIANCE.RECURRENCE.LABEL' | translate }}
      </span>
      <select class="fcomp-ap-filter-select"
              [ngModel]="recurrenceFilter"
              (ngModelChange)="setRecurrenceFilter($event)">
        <option [ngValue]="null">
          {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.FILTER_ANY_INTERVAL' | translate }}
        </option>
        <option *ngFor="let t of recurrenceTypes"
                [hidden]="t === Recurrence.None"
                [ngValue]="t">
          {{ recurrenceTypeKey(t) | translate }}
        </option>
      </select>
    </div>
  </div>

  <!-- States what is filtered, in words. Inferring it from which chips look
       pressed is guesswork, and unavailable to a screen reader. -->
  <div class="fcomp-ap-result-line" *ngIf="!loading && counts.allCount > 0">
    <span class="fcomp-ap-result-line__count" *ngIf="totalCount > 0">
      <!-- The server owns both numbers now: `rows` is one page, so counting it
           would report "10 of 10" no matter how long the list is. -->
      {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.SHOWING_RANGE' | translate:{ from: rangeStart, to: rangeEnd, total: totalCount } }}
    </span>
    <span class="fcomp-ap-result-line__filters" *ngIf="activeFilterLabels.length > 0">
      <ng-container *ngFor="let label of activeFilterLabels; let last = last">
        {{ label }}<span *ngIf="!last"> · </span>
      </ng-container>
    </span>
    <button type="button" class="fcomp-ap-result-line__clear"
            *ngIf="hasActiveFilters"
            (click)="clearAllFilters()">
      <i class="pi pi-times"></i>
      {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.CLEAR_FILTERS' | translate }}
    </button>
  </div>

  <!-- Compliance warning. Counted across the whole list, not the filtered
       view: a missed cycle must not be hideable behind a filter. -->
  <div class="fcomp-ap-compliance-warning" *ngIf="!loading && skippedCycleCount > 0">
    <i class="pi pi-exclamation-triangle"></i>
    <span>
      {{ 'FLEXI_COMPLIANCE.RECURRENCE.SKIPPED_WARNING' | translate:{ count: skippedCycleCount } }}
    </span>
  </div>

  <!-- Loading / error / empty states -->
  <div *ngIf="loading" class="fcomp-ap-state">
    <i class="pi pi-spin pi-spinner"></i>
    <span>{{ 'FLEXI_COMPLIANCE.LOADING' | translate }}</span>
  </div>

  <div class="fcomp-ap-error" *ngIf="error && !loading">{{ error }}</div>

  <!-- The two empty states are mutually exclusive by construction: allCount is
       the server's tally of every action point on the business, so "none exist"
       and "none match the filters" can never both be true. Keying the first on
       rows.length would show both at once, since a filtered-out page is also an
       empty page. -->
  <div class="fcomp-ap-empty" *ngIf="!loading && !error && counts.allCount === 0">
    <i class="pi pi-inbox"></i>
    <p>{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.EMPTY' | translate }}</p>
  </div>

  <div class="fcomp-ap-empty" *ngIf="!loading && !error && counts.allCount > 0 && totalCount === 0">
    <i class="pi pi-filter-slash"></i>
    <p>{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.EMPTY_FILTERED' | translate }}</p>
    <!-- The way out, next to the message that explains why the list is empty.
         The filter bar above can undo it too, but this is where the eye already
         is once the grid has gone. -->
    <button type="button" class="fcomp-ap-empty__clear"
            *ngIf="hasActiveFilters"
            (click)="clearAllFilters()">
      <i class="pi pi-times"></i>
      {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.CLEAR_FILTERS' | translate }}
    </button>
  </div>

  <!-- Column labels. The row below is a <ul>, not a <table>, so the header is
       a sibling that shares the same grid template. -->
  <div class="fcomp-ap-head" *ngIf="!loading && filteredRows.length > 0" aria-hidden="true">
    <span class="fcomp-ap-head__rail"></span>
    <span class="fcomp-ap-head__cell">{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_TITLE' | translate }}</span>
    <span class="fcomp-ap-head__cell fcomp-ap-head__cell--review">{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_REVIEW' | translate }}</span>
    <span class="fcomp-ap-head__cell">{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_RESPONSIBLE' | translate }}</span>
    <span class="fcomp-ap-head__cell">{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_DUE' | translate }}</span>
    <span class="fcomp-ap-head__cell">{{ 'FLEXI_COMPLIANCE.RECURRENCE.LABEL' | translate }}</span>
    <span class="fcomp-ap-head__cell fcomp-ap-head__cell--status">{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_STATUS' | translate }}</span>
  </div>

  <!-- Action point rows (one line each, fits the container) -->
  <ul class="fcomp-ap-grid" *ngIf="!loading && filteredRows.length > 0">
    <!-- The row is the edit affordance. Going Status → menu → Edit to change a
         title was three clicks for the most common action on the page; the
         status dropdown and its menu stop propagation, so they still work
         independently. Only rows the user may actually edit become
         interactive — a fake affordance is worse than none. -->
    <li *ngFor="let row of filteredRows"
        class="fcomp-ap-row"
        [class.is-completed]="row.status === ApStatus.Completed"
        [class.is-progress]="row.status === ApStatus.InProgress"
        [class.has-menu-open]="openMenuId === row.id"
        [class.is-clickable]="canManageRow(row)"
        [class.is-highlighted]="row.id === highlightedId"
        [attr.data-ap-row]="row.id"
        [attr.role]="canManageRow(row) ? 'button' : null"
        [attr.tabindex]="canManageRow(row) ? 0 : null"
        (click)="onRowClick(row)"
        (keyup.enter)="onRowClick(row)"
        [title]="row.description || row.sourceQuestion || ''">

      <!-- Left status rail -->
      <span class="fcomp-ap-row__rail" [ngClass]="statusToneClass(row.status)"></span>

      <!-- Title + (source as inline secondary text) -->
      <span class="fcomp-ap-row__title-cell">
        <span class="fcomp-ap-row__title">{{ row.title }}</span>
        <span class="fcomp-ap-row__source" *ngIf="row.sourceQuestion || row.sectionTitle">
          <i class="pi pi-bookmark"></i>
          <!-- Which finding this commitment answers for. Beside the question
               rather than in its own column: it qualifies the source, and it
               is only ever present when there IS a source. -->
          <span class="fcomp-ap-finding" *ngIf="row.sourceRating !== null"
                [ngClass]="findingToneClass(row.sourceRating)">
            {{ findingKey(row.sourceRating) | translate }}
          </span>
          <span class="fcomp-ap-row__source-s" *ngIf="row.sectionTitle">{{ row.sectionTitle }}</span>
          <span class="fcomp-ap-row__source-q" *ngIf="row.sourceQuestion"> · {{ row.sourceQuestion }}</span>
        </span>
        <span class="fcomp-ap-row__source fcomp-ap-row__source--loose" *ngIf="!row.sourceQuestion && !row.sectionTitle">
          <i class="pi pi-flag"></i>
          {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.SOURCE_LOOSE' | translate }}
        </span>
      </span>

      <!-- Originating review -->
      <span class="fcomp-ap-row__cell fcomp-ap-row__cell--review">
        <span class="fcomp-ap-review" [class.is-current]="row.isCurrentReview">
          <i class="pi pi-verified" *ngIf="row.isCurrentReview"></i>
          <i class="pi pi-history" *ngIf="!row.isCurrentReview"></i>
          <span class="fcomp-ap-review__code">{{ row.catalogVersionCode || '—' }}</span>
        </span>
        <small class="fcomp-ap-review__date" *ngIf="row.reviewCompletedOn || row.reviewCreatedOn">
          {{ (row.reviewCompletedOn || row.reviewCreatedOn) | date:'mediumDate' }}
        </small>
      </span>

      <!-- Responsible -->
      <span class="fcomp-ap-row__cell fcomp-ap-row__cell--responsible">
        <span class="fcomp-ap-badge fcomp-ap-badge--responsible"
              *ngIf="responsibleKey(row.responsibleParty) as rk"
              [ngClass]="row.responsibleParty === Responsible.Advisor ? 'is-advisor' : 'is-customer'"
              [attr.title]="(rk | translate) + responsibleNameSuffix(row)">
          <i class="pi" [ngClass]="row.responsibleParty === Responsible.Advisor ? 'pi-user' : 'pi-briefcase'"></i>
          <!-- Role plus the person who holds it: "OHS responsible - Kari
               Nordmann". Without the name the column says which side owes the
               work but never who.
               The text is its own element so it can ellipsis inside the column:
               the badge is nowrap, and a long name used to overflow the cell and
               paint over the DUE date beside it. Full value is in the title. -->
          <span class="fcomp-ap-badge__text">{{ rk | translate }}{{ responsibleNameSuffix(row) }}</span>
        </span>
        <span class="fcomp-ap-row__empty" *ngIf="!responsibleKey(row.responsibleParty)">—</span>
      </span>

      <!-- Due date -->
      <span class="fcomp-ap-row__cell fcomp-ap-row__cell--due">
        <span class="fcomp-ap-due" *ngIf="row.dueDate; else noDue"
              [class.is-overdue]="isOverdue(row)">
          <i class="pi pi-calendar"></i>
          {{ row.dueDate | date:'mediumDate' }}
        </span>
        <ng-template #noDue><span class="fcomp-ap-row__empty">—</span></ng-template>
      </span>

      <!-- Recurrence: interval, plus the compliance markers that matter more
           than the interval itself (skipped cycles, a shifted schedule). -->
      <span class="fcomp-ap-row__cell fcomp-ap-row__cell--recurrence">
        <ng-container *ngIf="row.recurrenceType !== Recurrence.None; else noRecurrence">
          <span class="fcomp-ap-badge fcomp-ap-badge--recurrence">
            <i class="pi pi-refresh"></i>
            {{ row.recurrenceName || (recurrenceTypeKey(row.recurrenceType) | translate) }}
          </span>
          <span class="fcomp-ap-flag is-skipped"
                *ngIf="row.skippedCycles > 0"
                [pTooltip]="'FLEXI_COMPLIANCE.RECURRENCE.SKIPPED_TOOLTIP' | translate:{ count: row.skippedCycles }"
                tooltipPosition="top">
            <i class="pi pi-exclamation-triangle"></i>
            {{ row.skippedCycles }}
          </span>
          <span class="fcomp-ap-flag is-shifted"
                *ngIf="row.scheduleShifted"
                [pTooltip]="'FLEXI_COMPLIANCE.RECURRENCE.SHIFTED_TOOLTIP' | translate"
                tooltipPosition="top">
            <i class="pi pi-arrow-right-arrow-left"></i>
          </span>
        </ng-container>
        <ng-template #noRecurrence><span class="fcomp-ap-row__empty">—</span></ng-template>
      </span>

      <!-- Status badge + actions menu -->
      <span class="fcomp-ap-row__cell fcomp-ap-row__cell--status">
        <ng-container *ngIf="canEditRow(row); else readOnlyStatus">
          <div class="fcomp-ap-actions">
            <button type="button"
                    class="fcomp-ap-badge fcomp-ap-badge--status fcomp-ap-badge--button"
                    [ngClass]="statusToneClass(row.status)"
                    (click)="toggleMenu(row.id, $event)"
                    [disabled]="busyIds.has(row.id)"
                    [attr.aria-expanded]="openMenuId === row.id">
              <i class="pi" [ngClass]="busyIds.has(row.id) ? 'pi-spin pi-spinner' : statusIcon(row.status)"></i>
              {{ statusKey(row.status) | translate }}
              <i class="pi pi-chevron-down fcomp-ap-badge__caret"></i>
            </button>

            <div class="fcomp-ap-menu" *ngIf="openMenuId === row.id"
                 (click)="$event.stopPropagation()">
              <div class="fcomp-ap-menu__group-label">
                {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_STATUS' | translate }}
              </div>
              <button type="button"
                      *ngFor="let s of statusesFor(row)"
                      class="fcomp-ap-menu__item"
                      [ngClass]="statusToneClass(s)"
                      [class.is-current]="row.status === s"
                      (click)="changeStatus(row, s, $event)"
                      [disabled]="busyIds.has(row.id) || row.status === s">
                <i class="pi" [ngClass]="statusIcon(s)"></i>
                <span>{{ statusKey(s) | translate }}</span>
                <i class="pi pi-check fcomp-ap-menu__check" *ngIf="row.status === s"></i>
              </button>
              <!-- Edit / delete only while the action point is outstanding.
                   A completed one keeps its wording, owner and deadline, and
                   can no longer be removed — see canManageRow. -->
              <hr class="fcomp-ap-menu__divider" />
              <!-- Details is readable by anyone who can see the row, and is
                   where the completion audit lives — the grid deliberately has
                   no CompletedBy column. -->
              <button type="button"
                      class="fcomp-ap-menu__item"
                      (click)="openDetails(row, $event)">
                <i class="pi pi-info-circle"></i>
                <span>{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.DETAILS' | translate }}</span>
              </button>
              <!-- Timeline is readable by anyone who can see the row: it is the
                   audit trail, including who shifted a schedule and why. -->
              <button type="button"
                      class="fcomp-ap-menu__item"
                      (click)="openTimeline(row, $event)">
                <i class="pi pi-history"></i>
                <span>{{ 'FLEXI_COMPLIANCE.TIMELINE.TITLE' | translate }}</span>
              </button>

              <ng-container *ngIf="canManageRow(row)">
                <button type="button"
                        class="fcomp-ap-menu__item"
                        (click)="openEdit(row, $event)"
                        [disabled]="busyIds.has(row.id)">
                  <i class="pi pi-pencil"></i>
                  <span>{{ 'FLEXI_COMPLIANCE.ACTION.EDIT' | translate }}</span>
                </button>
                <button type="button"
                        class="fcomp-ap-menu__item fcomp-ap-menu__item--danger"
                        (click)="deleteRow(row, $event)"
                        [disabled]="busyIds.has(row.id)">
                  <i class="pi pi-trash"></i>
                  <span>{{ 'FLEXI_COMPLIANCE.ACTION.DELETE' | translate }}</span>
                </button>
              </ng-container>
            </div>
          </div>
        </ng-container>

        <ng-template #readOnlyStatus>
          <span class="fcomp-ap-badge fcomp-ap-badge--status"
                [ngClass]="statusToneClass(row.status)">
            <i class="pi" [ngClass]="statusIcon(row.status)"></i>
            {{ statusKey(row.status) | translate }}
          </span>
        </ng-template>
      </span>
    </li>
  </ul>

  <!--
    Server-side pagination, ten rows a page. The controls are deliberately
    plain: page count comes from the server with the page itself, so the widget
    never has to guess how many there are.
  -->
  <nav class="fcomp-ap-pager" *ngIf="!loading && totalPages > 1"
       [attr.aria-label]="'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.PAGINATION' | translate">
    <button type="button" class="fcomp-ap-pager__btn"
            (click)="goToPage(pageNumber - 1)"
            [disabled]="!canGoPrevious"
            [attr.title]="'PREVIOUS' | translate">
      <i class="pi pi-chevron-left"></i>
    </button>
    <span class="fcomp-ap-pager__status">
      {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.PAGE_OF' | translate:{ page: pageNumber, pages: totalPages } }}
    </span>
    <button type="button" class="fcomp-ap-pager__btn"
            (click)="goToPage(pageNumber + 1)"
            [disabled]="!canGoNext"
            [attr.title]="'NEXT' | translate">
      <i class="pi pi-chevron-right"></i>
    </button>
  </nav>
</div>

<!--
  Read-only details view.

  This is the ONLY place the completion audit is shown. The acceptance criterion
  is explicit that CompletedBy must not be a grid column, and the reason holds
  up: the grid is a worklist about what is still owed, and a "who closed this"
  column would be blank on every row that matters while quietly publishing a
  name on the ones that don't.
-->
<p-dialog
  [visible]="detailRow !== null"
  (visibleChange)="!$event && closeDetails()"
  [modal]="true"
  [draggable]="false"
  [style]="{ width: '560px', maxWidth: '95vw' }"
  styleClass="fcomp-ap-detail-dialog"
  [header]="'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.DETAILS_TITLE' | translate">

  <div class="fcomp-ap-detail" *ngIf="detailRow">
    <h4 class="fcomp-ap-detail__title">{{ detailRow.title }}</h4>
    <p class="fcomp-ap-detail__desc" *ngIf="detailRow.description">{{ detailRow.description }}</p>

    <dl class="fcomp-ap-detail__grid">
      <dt>{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_STATUS' | translate }}</dt>
      <dd>{{ statusKey(detailRow.status) | translate }}</dd>

      <dt>{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_RESPONSIBLE' | translate }}</dt>
      <dd>
        <ng-container *ngIf="responsibleKey(detailRow.responsibleParty) as rk">
          {{ rk | translate }}{{ responsibleNameSuffix(detailRow) }}
        </ng-container>
        <span *ngIf="!responsibleKey(detailRow.responsibleParty)">
          {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.NOT_SET' | translate }}
        </span>
      </dd>

      <dt>{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_HSE_ADVISOR' | translate }}</dt>
      <dd>{{ detailRow.hseAdvisorName || ('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.NOT_SET' | translate) }}</dd>

      <dt>{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_DUE' | translate }}</dt>
      <dd>{{ (detailRow.dueDate | date:'dd-MM-yyyy') || ('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.NOT_SET' | translate) }}</dd>

      <dt *ngIf="detailRow.recurrenceType !== Recurrence.None">
        {{ 'FLEXI_COMPLIANCE.RECURRENCE.LABEL' | translate }}
      </dt>
      <dd *ngIf="detailRow.recurrenceType !== Recurrence.None">{{ detailRow.recurrenceName }}</dd>

      <dt>{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_SOURCE' | translate }}</dt>
      <dd>
        <ng-container *ngIf="detailRow.sourceQuestion; else looseSource">
          <span *ngIf="detailRow.sectionTitle">{{ detailRow.sectionTitle }} — </span>{{ detailRow.sourceQuestion }}
          <span class="fcomp-ap-finding" *ngIf="detailRow.sourceRating !== null"
                [ngClass]="findingToneClass(detailRow.sourceRating)">
            {{ findingKey(detailRow.sourceRating) | translate }}
          </span>
        </ng-container>
        <ng-template #looseSource>
          {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.SOURCE_LOOSE' | translate }}
        </ng-template>
      </dd>

      <dt>{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_REVIEW' | translate }}</dt>
      <dd>{{ detailRow.catalogVersionCode }}</dd>

      <dt>{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_CREATED' | translate }}</dt>
      <dd>
        {{ detailRow.createdOn | date:'dd MMM yyyy' }}
        <span *ngIf="detailRow.createdByName">
          · {{ detailRow.createdByName }}
        </span>
      </dd>
    </dl>

    <!-- The completion audit. Rendered only when the action point has actually
         been completed, so an outstanding one shows no empty "completed by"
         row implying somebody was meant to have closed it. -->
    <div class="fcomp-ap-detail__completed" *ngIf="detailRow.completedByName || detailRow.completedOn">
      <i class="pi pi-check-circle"></i>
      <span>
        <strong>{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COMPLETED_BY_LABEL' | translate }}:</strong>
        {{ detailRow.completedByName || '—' }}
        <ng-container *ngIf="detailRow.completedOn">
          · {{ detailRow.completedOn | date:'dd MMM yyyy HH:mm' }}
        </ng-container>
      </span>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <button type="button" class="btn btn-secondary" (click)="closeDetails()">
      <span translate>CLOSE</span>
    </button>
  </ng-template>
</p-dialog>

<!-- Detail editor for an outstanding action point (CSM-only) -->
<p-dialog
  [visible]="editRow !== null"
  (visibleChange)="!$event && closeEdit()"
  [modal]="true"
  [closable]="!editSaving"
  [draggable]="false"
  [style]="{ width: '560px', maxWidth: '95vw' }"
  styleClass="fcomp-ap-edit-dialog"
  [header]="'FLEXI_COMPLIANCE.ACTION.EDIT' | translate">

  <div class="fcomp-ap-edit" *ngIf="editRow">
    <!-- Which checkpoint this commitment answers for. Read-only, and first in
         the dialog: editing a tiltak without seeing the question it was raised
         under is how one ends up describing the wrong finding. -->
    <div class="fcomp-ap-edit__source">
      <span class="fcomp-ap-edit__source-label">
        {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_SOURCE' | translate }}
      </span>
      <span class="fcomp-ap-edit__source-value"
            *ngIf="editRow.sourceQuestion || editRow.sectionTitle; else looseEditSource">
        <span class="fcomp-ap-finding" *ngIf="editRow.sourceRating !== null"
              [ngClass]="findingToneClass(editRow.sourceRating)">
          {{ findingKey(editRow.sourceRating) | translate }}
        </span>
        <span *ngIf="editRow.sectionTitle">{{ editRow.sectionTitle }}</span>
        <span *ngIf="editRow.sectionTitle && editRow.sourceQuestion"> — </span>
        <span *ngIf="editRow.sourceQuestion">{{ editRow.sourceQuestion }}</span>
      </span>
      <ng-template #looseEditSource>
        <span class="fcomp-ap-edit__source-value is-loose">
          {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.SOURCE_LOOSE' | translate }}
        </span>
      </ng-template>
    </div>

    <label class="fcomp-ap-edit__field">
      <span>{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_TITLE' | translate }}</span>
      <input type="text" class="fcomp-input"
             [(ngModel)]="editForm.title"
             [placeholder]="'FLEXI_COMPLIANCE.ACTION.TITLE_PLACEHOLDER' | translate" />
    </label>

    <label class="fcomp-ap-edit__field">
      <span>{{ 'DESCRIPTION' | translate }}</span>
      <textarea class="fcomp-comment" rows="3"
                [(ngModel)]="editForm.description"
                [placeholder]="'FLEXI_COMPLIANCE.ACTION.DESC_PLACEHOLDER' | translate"></textarea>
    </label>

    <div class="fcomp-ap-edit__row">
      <label class="fcomp-ap-edit__field">
        <span>{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_RESPONSIBLE' | translate }}</span>
        <select class="fcomp-input" [(ngModel)]="editForm.responsible">
          <option [ngValue]="Responsible.Customer">{{ 'FLEXI_COMPLIANCE.RESPONSIBLE.CUSTOMER' | translate }}</option>
          <option [ngValue]="Responsible.Advisor">{{ 'FLEXI_COMPLIANCE.RESPONSIBLE.ADVISOR' | translate }}</option>
        </select>
      </label>
      <label class="fcomp-ap-edit__field">
        <span>{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_DUE' | translate }}</span>
        <input type="date" class="fcomp-input" [(ngModel)]="editForm.due" />
      </label>
    </div>

    <label class="fcomp-ap-edit__field">
      <span>{{ 'FLEXI_COMPLIANCE.RECURRENCE.LABEL' | translate }}</span>
      <select class="fcomp-input" [(ngModel)]="editForm.recurrenceType">
        <option *ngFor="let t of recurrenceTypes" [ngValue]="t">
          {{ recurrenceTypeKey(t) | translate }}
        </option>
      </select>
      <small class="fcomp-ap-edit__hint" *ngIf="editForm.recurrenceType !== Recurrence.None">
        {{ 'FLEXI_COMPLIANCE.RECURRENCE.EDIT_HINT' | translate }}
      </small>
    </label>

    <!-- Read-only: the follow-up person comes from the business's HSE advisor
         and is snapshotted per action point, so it is shown, not edited here. -->
    <div class="fcomp-ap-edit__readonly" *ngIf="editRow.hseAdvisorName">
      <span>{{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_HSE_ADVISOR' | translate }}</span>
      <strong>{{ editRow.hseAdvisorName }}</strong>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <button type="button" class="fcomp-btn fcomp-btn--ghost"
            [disabled]="editSaving" (click)="closeEdit()">
      {{ 'CANCEL' | translate }}
    </button>
    <button type="button" class="fcomp-btn fcomp-btn--primary"
            [disabled]="editSaving || !editForm.title.trim()"
            (click)="saveEdit()">
      <i class="pi" [ngClass]="editSaving ? 'pi-spin pi-spinner' : 'pi-check'"></i>
      {{ 'FLEXI_COMPLIANCE.ACTION.SAVE' | translate }}
    </button>
  </ng-template>
</p-dialog>

<!-- ===================================================================
     Overdue recurring completion — how should the next deadline be set?
     Both buttons name the deadline they will actually produce; abstract
     labels here are how people shift a compliance cycle by accident.
     =================================================================== -->
<p-dialog
  [visible]="scheduleChoiceRow !== null"
  (visibleChange)="!$event && closeScheduleChoice()"
  [modal]="true"
  [draggable]="false"
  [style]="{ width: '600px', maxWidth: '95vw' }"
  [header]="'FLEXI_COMPLIANCE.RECURRENCE.SCHEDULE_DIALOG_TITLE' | translate">

  <div class="fcomp-schedule-choice" *ngIf="schedulePreview as preview">
    <p class="fcomp-schedule-choice__intro">
      {{ 'FLEXI_COMPLIANCE.RECURRENCE.SCHEDULE_DIALOG_INTRO' | translate:{
           days: preview.daysOverdue,
           recurrence: preview.recurrenceName
         } }}
    </p>

    <!-- Option A, pre-selected: in HSE many deadlines are legally tied to a
         time of year, so the safe default leaves the cycle where it is. -->
    <label class="fcomp-schedule-option"
           [class.is-selected]="selectedScheduleMode === ScheduleMode.OriginalSchedule">
      <input type="radio" name="scheduleMode"
             [value]="ScheduleMode.OriginalSchedule"
             [(ngModel)]="selectedScheduleMode" />
      <span class="fcomp-schedule-option__body">
        <span class="fcomp-schedule-option__title">
          {{ 'FLEXI_COMPLIANCE.RECURRENCE.OPTION_ORIGINAL' | translate }}
          <span class="fcomp-schedule-option__recommended">
            {{ 'FLEXI_COMPLIANCE.RECURRENCE.RECOMMENDED' | translate }}
          </span>
        </span>
        <span class="fcomp-schedule-option__date">
          {{ 'FLEXI_COMPLIANCE.RECURRENCE.NEXT_DUE' | translate }}
          <strong>{{ preview.originalScheduleNextDueDate | date:'dd MMM yyyy' }}</strong>
        </span>
        <span class="fcomp-schedule-option__note is-warning"
              *ngIf="preview.originalScheduleSkippedCycles > 0">
          <i class="pi pi-exclamation-triangle"></i>
          {{ 'FLEXI_COMPLIANCE.RECURRENCE.OPTION_ORIGINAL_SKIPPED' | translate:{
               count: preview.originalScheduleSkippedCycles
             } }}
        </span>
      </span>
    </label>

    <label class="fcomp-schedule-option"
           [class.is-selected]="selectedScheduleMode === ScheduleMode.ShiftSchedule">
      <input type="radio" name="scheduleMode"
             [value]="ScheduleMode.ShiftSchedule"
             [(ngModel)]="selectedScheduleMode" />
      <span class="fcomp-schedule-option__body">
        <span class="fcomp-schedule-option__title">
          {{ 'FLEXI_COMPLIANCE.RECURRENCE.OPTION_SHIFT' | translate }}
        </span>
        <span class="fcomp-schedule-option__date">
          {{ 'FLEXI_COMPLIANCE.RECURRENCE.NEXT_DUE' | translate }}
          <strong>{{ preview.shiftScheduleNextDueDate | date:'dd MMM yyyy' }}</strong>
        </span>
        <!-- Shifting is permanent, and that is the part users miss. -->
        <span class="fcomp-schedule-option__note is-caution">
          <i class="pi pi-info-circle"></i>
          {{ 'FLEXI_COMPLIANCE.RECURRENCE.OPTION_SHIFT_WARNING' | translate }}
        </span>
      </span>
    </label>
  </div>

  <ng-template pTemplate="footer">
    <button type="button" class="fcomp-btn fcomp-btn--ghost" (click)="closeScheduleChoice()">
      {{ 'CANCEL' | translate }}
    </button>
    <button type="button" class="fcomp-btn fcomp-btn--primary" (click)="confirmScheduleChoice()">
      <i class="pi pi-check"></i>
      {{ 'FLEXI_COMPLIANCE.RECURRENCE.CONFIRM_COMPLETE' | translate }}
    </button>
  </ng-template>
</p-dialog>

<!-- ===================================================================
     Audit timeline
     =================================================================== -->
<p-dialog
  [visible]="timelineRow !== null"
  (visibleChange)="!$event && closeTimeline()"
  [modal]="true"
  [draggable]="false"
  [style]="{ width: '620px', maxWidth: '95vw' }"
  [header]="'FLEXI_COMPLIANCE.TIMELINE.TITLE' | translate">

  <div class="fcomp-timeline" *ngIf="timelineRow">
    <p class="fcomp-timeline__subject">{{ timelineRow.title }}</p>

    <div class="fcomp-timeline__state" *ngIf="timelineLoading">
      <i class="pi pi-spin pi-spinner"></i>
      {{ 'FLEXI_COMPLIANCE.LOADING' | translate }}
    </div>

    <div class="fcomp-timeline__state" *ngIf="!timelineLoading && timelineEntries.length === 0">
      {{ 'FLEXI_COMPLIANCE.TIMELINE.EMPTY' | translate }}
    </div>

    <ol class="fcomp-timeline__list" *ngIf="!timelineLoading && timelineEntries.length > 0">
      <li class="fcomp-timeline__entry" *ngFor="let entry of timelineEntries">
        <span class="fcomp-timeline__dot"></span>
        <div class="fcomp-timeline__body">
          <p class="fcomp-timeline__message">{{ entry.message }}</p>
          <p class="fcomp-timeline__meta">
            {{ entry.createdOn | date:'dd MMM yyyy HH:mm' }}
            <ng-container *ngIf="entry.createdByName">
              · {{ entry.createdByName }}
            </ng-container>
            <ng-container *ngIf="!entry.createdBy">
              · {{ 'FLEXI_COMPLIANCE.TIMELINE.SYSTEM' | translate }}
            </ng-container>
          </p>
        </div>
      </li>
    </ol>
  </div>

  <ng-template pTemplate="footer">
    <button type="button" class="fcomp-btn fcomp-btn--ghost" (click)="closeTimeline()">
      {{ 'CLOSE' | translate }}
    </button>
  </ng-template>
</p-dialog>

<p-confirmDialog
  [header]="'CONFIRMATION' | translate"
  icon="pi pi-question-circle"
  [style]="{ width: '460px' }"
  [acceptLabel]="'SWITCH_YES' | translate"
  [rejectLabel]="'SWITCH_NO' | translate">
</p-confirmDialog>
